import { AxiosError, AxiosResponse } from 'axios';
import React from 'react';

export interface PaginatedResponse<T> {
    items: T[];
    nextToken?: string;
}

export interface UsePaginatedFetchResult<T> {
    /** All items accumulated across pages */
    items: T[];
    /** Token for the next page, undefined when no more pages */
    nextToken: string | undefined;
    /** True during the first fetch (no items loaded yet) */
    isInitialLoading: boolean;
    /** True while fetching an additional page */
    isLoadingMore: boolean;
    /** Most recent error, or undefined. Persists until the next successful fetch. */
    error: AxiosError | undefined;
    /** Fetch the next page using the current nextToken */
    loadMore: () => void;
    /** Reset all state and re-fetch from the beginning */
    reset: () => void;
}

/**
 * Generic hook for paginated API fetching with append-on-load-more semantics.
 *
 * Race-condition safety: each call to fetchPage captures a unique request ID
 * from a monotonically-increasing counter. When the response arrives, it checks
 * whether the counter still matches. Any newer fetch (from deps change, reset,
 * or loadMore) automatically invalidates all prior in-flight requests.
 *
 * Error handling: errors persist in state until the next successful response
 * clears them. Consumers should derive visibility from the error and loading
 * states (e.g. `error && !isLoadingMore`) rather than relying on the hook to
 * clear errors eagerly before each request.
 *
 * @param fetchFn       Async function that calls the API. Receives an optional
 *                      pagination token and returns an Axios response whose
 *                      `data` includes an optional `nextToken`.
 * @param extractItems  Pulls the items array out of the response data.
 * @param deps          Dependency array — when any value changes the hook
 *                      resets and re-fetches from the first page.
 */
export function usePaginatedFetch<T>(
    fetchFn: (token?: string) => Promise<AxiosResponse<{ nextToken?: string }>>,
    extractItems: (data: any) => T[],
    deps: React.DependencyList = []
): UsePaginatedFetchResult<T> {
    const [items, setItems] = React.useState<T[]>([]);
    const [nextToken, setNextToken] = React.useState<string | undefined>(undefined);
    const [isInitialLoading, setIsInitialLoading] = React.useState<boolean>(true);
    const [isLoadingMore, setIsLoadingMore] = React.useState<boolean>(false);
    const [error, setError] = React.useState<AxiosError | undefined>(undefined);

    // Refs for fetchFn and extractItems so fetchPage always calls the latest
    // versions without needing them in the useCallback dependency array. This
    // decouples "when to re-fetch" (deps) from "which function to call" (always
    // latest), preventing stale closures if a consumer's fetchFn captures
    // mutable state not listed in deps.
    const fetchFnRef = React.useRef(fetchFn);
    fetchFnRef.current = fetchFn;

    const extractItemsRef = React.useRef(extractItems);
    extractItemsRef.current = extractItems;

    // Monotonically-increasing counter that uniquely identifies each fetch
    // cycle. Every call to fetchPage captures the current counter value; when
    // the response arrives it checks whether the counter still matches. If a
    // newer fetch was started (by deps change, reset, or loadMore) the old
    // response is silently discarded.
    const requestIdRef = React.useRef(0);

    const fetchPage = React.useCallback(
        async (token?: string) => {
            const myId = ++requestIdRef.current;
            const isInitialLoad = !token;

            if (isInitialLoad) {
                setIsInitialLoading(true);
            } else {
                setIsLoadingMore(true);
            }

            try {
                const response = await fetchFnRef.current(token);
                if (requestIdRef.current !== myId) return;

                const data = response.data;
                const newItems = extractItemsRef.current(data);
                setItems((prev) => (isInitialLoad ? newItems : [...prev, ...newItems]));
                setNextToken(data.nextToken);
                setError(undefined);
            } catch (e: unknown) {
                if (requestIdRef.current !== myId) return;
                setError(e as AxiosError);
            } finally {
                if (requestIdRef.current === myId) {
                    setIsInitialLoading(false);
                    setIsLoadingMore(false);
                }
            }
        },
        // deps controls when to re-fetch; refs ensure the latest
        // fetchFn/extractItems are always used regardless of deps
        deps
    );

    const nextTokenRef = React.useRef(nextToken);
    nextTokenRef.current = nextToken;

    const loadMore = React.useCallback(() => {
        fetchPage(nextTokenRef.current);
    }, [fetchPage]);

    const reset = React.useCallback(() => {
        setItems([]);
        setNextToken(undefined);
        setError(undefined);
        setIsInitialLoading(true);
        setIsLoadingMore(false);
        fetchPage();
    }, [fetchPage]);

    React.useEffect(() => {
        // Fresh effect cycle — reset state for the new fetch.
        // No need to manage cancelledRef — the counter inside fetchPage
        // automatically invalidates any prior in-flight requests.
        setItems([]);
        setNextToken(undefined);
        setError(undefined);
        fetchPage();
    }, [fetchPage]);

    return { items, nextToken, isInitialLoading, isLoadingMore, error, loadMore, reset };
}
